import { format, parseISO } from 'date-fns';

/**
 * Format a date string from the database to display in the UI
 * Handles DATE fields without time component correctly
 * @param {string} dateString - Date string from database (e.g., "2025-12-11")
 * @param {string} formatStr - Format string for date-fns (default: "MMM dd, yyyy")
 * @returns {string} Formatted date string
 */
export const formatDate = (dateString, formatStr = 'MMM dd, yyyy') => {
  if (!dateString) return '-';

  try {
    // If it's just a date (YYYY-MM-DD) without time, treat it as local date
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [year, month, day] = dateString.split('-').map(Number);
      const localDate = new Date(year, month - 1, day);
      return format(localDate, formatStr);
    }

    // Otherwise parse as ISO string (includes time)
    return format(parseISO(dateString), formatStr);
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateString;
  }
};

/**
 * Format a datetime string from the database to display in the UI
 * @param {string} dateTimeString - DateTime string from database
 * @param {string} formatStr - Format string for date-fns (default: "MMM dd, yyyy h:mm a")
 * @returns {string} Formatted datetime string
 */
export const formatDateTime = (dateTimeString, formatStr = 'MMM dd, yyyy h:mm a') => {
  if (!dateTimeString) return '-';

  try {
    return format(parseISO(dateTimeString), formatStr);
  } catch (error) {
    console.error('Error formatting datetime:', error);
    return dateTimeString;
  }
};

/**
 * Format a time string (e.g., "14:30:00") to display format
 * @param {string} timeString - Time string from database (HH:MM:SS)
 * @param {string} formatStr - Format string (default: "h:mm a")
 * @returns {string} Formatted time string
 */
export const formatTime = (timeString, formatStr = 'h:mm a') => {
  if (!timeString) return '-';

  try {
    // Parse time string as today's date with that time
    const [hours, minutes] = timeString.split(':').map(Number);
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return format(date, formatStr);
  } catch (error) {
    console.error('Error formatting time:', error);
    return timeString;
  }
};
